// แหล่งนักล่าและเหยื่อของหน้าเกม: ชนิดจากฐานข้อมูลในช่อง 5° ที่เราอยู่ (ถ้ามี) ตามด้วยรายชื่อที่คัดไว้
// ดึงจากฐานข้อมูลเบื้องหลัง ครั้งแรกที่เข้าช่องใหม่ใช้รายชื่อที่คัดไว้ไปก่อน turn ถัดไปจึงได้ชนิดจากฐานข้อมูล

import { FAUNA, type Medium } from "../data/fauna";
import { dietOf, sizeOf } from "../data/food-web";
import { resolveTraits } from "../data/trait-defaults";
import type { Habitat, LifeState, SpeciesInfo, StageDef, Traits } from "../engine/types";
import { relevantSubgroups, withNeighbors, type NeighborSource } from "../geo/neighbors";
import { tileOfCell } from "../geo/tiles";

interface Row {
  id: number;
  sci: string;
  name_th: string | null;
  subgroup_id: string;
  traits: string | null;
}

function mediaOf(habitat: readonly Habitat[]): Medium[] {
  const out = new Set<Medium>();
  for (const h of habitat) {
    if (h === "sea") out.add("sea");
    else if (h === "freshwater") out.add("freshwater");
    else {
      out.add("land");
      if (h === "wetland") out.add("freshwater");
    }
  }
  return [...out];
}

function toSource(row: Row): NeighborSource | null {
  let known: Partial<Traits> = {};
  try {
    known = row.traits ? (JSON.parse(row.traits) as Partial<Traits>) : {};
  } catch {
    // ใช้ค่าประมาณของกลุ่มย่อยแทน
  }
  try {
    const traits = resolveTraits(row.subgroup_id, known);
    const base = { sci: row.sci, subgroupId: row.subgroup_id, traits };
    const diet = dietOf(base);
    return {
      sci: row.sci,
      nameTh: row.name_th ?? row.sci,
      subgroupId: row.subgroup_id,
      size: sizeOf(base),
      media: mediaOf(traits.habitat),
      habitat: traits.habitat,
      ...(diet ? { diet } : {}),
    };
  } catch {
    return null;
  }
}

const cache = new Map<string, NeighborSource[]>();
const loading = new Set<string>();

function load(key: string, tile: number, subgroups: string[]): void {
  if (loading.has(key)) return;
  loading.add(key);
  fetch(`/api/neighbors?tile=${tile}&sg=${subgroups.join(",")}`)
    .then((res) => (res.ok ? (res.json() as Promise<{ species: Row[] }>) : { species: [] }))
    .then((body) => cache.set(key, body.species.map(toSource).filter((s): s is NeighborSource => s !== null)))
    .catch(() => cache.set(key, []))
    .finally(() => loading.delete(key));
}

/** ชนิดที่อาจเป็นนักล่าหรือเหยื่อแถวช่องนี้ */
export function sourcesAt(species: SpeciesInfo, stage: StageDef, cell: number): readonly NeighborSource[] {
  const { predators, prey } = relevantSubgroups(species, stage);
  const subgroups = [...new Set([...predators, ...prey])].sort();
  if (!subgroups.length) return FAUNA;
  const tile = tileOfCell(cell);
  const key = `${tile}:${subgroups.join(",")}`;
  const fromDb = cache.get(key);
  if (!fromDb) load(key, tile, subgroups);
  return fromDb?.length ? [...fromDb, ...FAUNA] : FAUNA;
}

/** อัปเดตนักล่าและเหยื่อแถวนี้หลังย้ายตำแหน่ง */
export function withLocalWildlife(life: LifeState, stage: StageDef): LifeState {
  if (!life.geo) return life;
  return withNeighbors(life, stage, { sources: sourcesAt(life.species, stage, life.geo.cell) });
}
